import { IManageTagsInfo } from '../../../pages/mylibrary/components/manageTagsSnackBar';
import { ILibraryMainStore } from '../../../pages/mylibrary/typings/stores';
import LookupApiService from '../../api/lookupApiService';
import { ISaveUserSettingRequest } from '../../api/models/user';
import UserApiService from '../../api/userApiService';
import { ITagsStore } from '../../stores/tagsStore';
import { textTruncate } from '../../styles/commonStyles';
import { ISafeExecuteAsyncAction } from '../../typings/stores';
import { TagEditorMode } from '../../typings/tags/tagEditorMode';
import TagItem from '../../typings/tags/tagItem';
import { TagsSortOrder } from '../../typings/tags/tagsSortOrder';
import TagsFetcher from '../../utils/tagsFetcher';
import TagUtils from '../../utils/tagUtils';
import ApplyButton from '../tags/applyButton';
import useAvailableTags, { IAvailableTag } from '../tags/hooks/useAvailableTags';
import PendingTagsInfo from '../tags/pendingTagsInfo';
import TagsInput from '../tags/tagsInput';
import ModalWindow from './modalWindow';
import { Box, Stack } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Colors } from 'copyright-clearance-center-common-ui';
import { DialogActions } from 'copyright-clearance-center-common-ui-react/dist/components/rupDialog';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';

export interface IManageTagsDialogProps {
	onCancel: () => void;
	onApply: (info: IManageTagsInfo) => void;
	libraryId?: number;
	tagsStore?: ITagsStore;
	lookupApiService?: LookupApiService;
	userApiService?: UserApiService;
	sortOrder?: TagsSortOrder;
	setSortOrder?: (order: TagsSortOrder) => void;
	safeExecuteAsync?: ISafeExecuteAsyncAction;
}

const useStyles = makeStyles(() =>
	createStyles({
		tagsInputContainer: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between'
		},
		inputHelperText: {
			color: Colors.neutral600,
			fontSize: '0.75rem',
			fontWeight: '400',
			padding: '0 0.75rem',
			marginTop: '0.25rem'
		},
		tagsListContainer: {
			border: `1px solid ${Colors.neutral200}`,
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			minHeight: '0',
			minWidth: '0'
		},
		captionContainer: {
			alignItems: 'center',
			backgroundColor: Colors.neutral50,
			display: 'flex',
			height: '2.25rem',
			justifyContent: 'space-between',
			padding: '0.125rem 0.75rem',
			flex: 'none'
		},
		caption: {
			color: Colors.neutral900,
			fontSize: '0.875rem',
			fontWeight: '500',
			minWidth: '0',
			...textTruncate
		},
		list: {
			backgroundColor: '#fff',
			display: 'flex',
			flex: '1',
			flexDirection: 'column',
			overflowY: 'auto'
		}
	})
);

export const ManageTagsDialog: React.FunctionComponent<IManageTagsDialogProps> = props => {
	const classes = useStyles();

	const [inputValue, setInputValue] = React.useState('');
	const searchTimeout = React.useRef<NodeJS.Timeout>(null);
	const filterRef = React.useRef('');
	const [applying, setApplying] = React.useState(false);
	const timeoutMilliseconds = 1000;

	const [removeCount, setRemoveCount] = useState(0);
	const [updateCount, setUpdateCount] = useState(0);

	const inputRef = React.useRef<HTMLInputElement>(null);

	const createFetcher = () => {
		return new TagsFetcher({
			api: props.lookupApiService,
			filter: filterRef.current,
			libraryId: props.libraryId,
			sort: props.sortOrder
		});
	};

	const [availableTagsElement, availableTags, newTags, setNewTags, , , setFetcher, isTagsLoading, isTagInAction] =
		useAvailableTags({
			fetcher: createFetcher(),
			tagEditorMode: TagEditorMode.Full,
			safeExecuteAsync: props.safeExecuteAsync,
			tagsEqual: TagUtils.equals,
			tagIncludes: TagUtils.includes,
			onSortOrderChange: async order => {
				props.setSortOrder(order);

				const request: ISaveUserSettingRequest = {
					settingKey: 'MyLibraryManageTagsDlg',
					updatedSettings: { TagsSortBy: order }
				};

				await props.safeExecuteAsync(async () => await props.userApiService.saveUserSetting(request), null);
			},
			sortOrder: props.sortOrder,
			width: '100%',
			height: '23rem',
			onExistingTagRemove: removeExistingTag,
			onExistingTagUpdate: updateExistingTag,
			header: localize('Available tags')
		});

	function removeExistingTag(tag: IAvailableTag): void {
		const previouslyUpdatedTag = props.tagsStore.existingTags.find(existingTag => existingTag.id === tag.id);
		if (!!previouslyUpdatedTag) {
			previouslyUpdatedTag.updatedName = undefined;
			previouslyUpdatedTag.deleted = true;
			setUpdateCount(updateCount - 1);
		} else {
			const tagItem = new TagItem(tag.id, tag.tag);
			tagItem.deleted = true;
			props.tagsStore.addExistingTag(tagItem);
		}

		setRemoveCount(removeCount + 1);
	}

	function updateExistingTag(tag: IAvailableTag, updatedName: string): void {
		const tagIndex = props.tagsStore.existingTags.findIndex(existingTag => existingTag.id === tag.id);
		const isPreviouslyUpdated = tagIndex !== -1;

		if (isPreviouslyUpdated) {
			const updatedTag = props.tagsStore.existingTags[tagIndex];
			updatedTag.updatedName = updatedName;

			const isUpdatedBackToOriginalName = TagUtils.equals(updatedTag.updatedName, updatedTag.originalName);
			if (isUpdatedBackToOriginalName) {
				props.tagsStore.existingTags.splice(tagIndex, 1);
				setUpdateCount(updateCount - 1);
			}
		} else {
			const tagItem = new TagItem(tag.id, tag.tag);
			tagItem.updatedName = updatedName;
			props.tagsStore.addExistingTag(tagItem);
			setUpdateCount(updateCount + 1);
		}
	}

	const updateFetcher = (callback?: () => boolean) => {
		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		searchTimeout.current = setTimeout(() => {
			if (callback && !callback()) {
				return;
			}

			setFetcher(createFetcher());
		}, timeoutMilliseconds);
	};

	React.useEffect(() => {
		if (!isTagInAction) {
			inputRef.current?.focus();
		}
	}, [inputRef.current, isTagInAction]);

	React.useEffect(() => {
		setFetcher(createFetcher());
	}, [props.sortOrder]);

	React.useEffect(() => {
		props.tagsStore.setNewTags(newTags.map(tag => new TagItem(TagItem.NewTagId, tag)));
	}, [newTags]);

	const cancel = () => {
		props.tagsStore.requestCancellation();
		props.tagsStore.resetExistingTags();
		props.onCancel();
	};

	const apply = async () => {
		setApplying(true);
		const result = await props.safeExecuteAsync(async () => await props.tagsStore.applyChanges(), null);
		setApplying(false);

		if (result) {
			props.tagsStore.resetExistingTags();

			const newAppliedTagsInfo: IManageTagsInfo = {
				newTags: newTags.length,
				renamedTags: updateCount,
				deletedTags: removeCount,
				addDuplicationsCount: result.notAddedTagsCount,
				renameDuplicationsCount: result.notRenamedTagsCount
			};

			props.onApply(newAppliedTagsInfo);
		}
	};

	const changesCount = newTags.length + removeCount + updateCount;
	const hasChanges = changesCount > 0;

	const actions: DialogActions = {
		left: [
			<Box key="changes-info" marginLeft={-2}>
				<PendingTagsInfo
					textSx={{
						fontSize: '0.75rem',
						fontStyle: 'normal',
						fontWeight: 400,
						lineHeight: '1rem',
						color: Colors.neutral600
					}}
					pendingTagsCount={newTags.length}
					updatedTagsCount={updateCount}
					removedTagsCount={removeCount}
				/>
			</Box>
		],
		right: [
			<Stack key="actions" direction="row" flexWrap="nowrap">
				<button
					className="rup-btn rup-btn--lg"
					data-test-id="manage-tags-cancel-btn"
					type="button"
					onClick={cancel}
					disabled={applying}
				>
					{localize('Cancel')}
				</button>
				<ApplyButton
					applying={applying}
					disabled={!hasChanges}
					onClick={!applying ? apply : null}
					changesCount={changesCount}
					testId="manage-tags-apply-btn"
					large
				/>
			</Stack>
		]
	};
	const content = (
		<>
			<div className={classes.tagsInputContainer}>
				<TagsInput
					inputValue={inputValue}
					onInput={(input: string) => {
						setInputValue(input);

						if (searchTimeout.current) {
							clearTimeout(searchTimeout.current);
						}

						updateFetcher(() => {
							if (TagUtils.equals(input, filterRef.current)) {
								return false;
							}

							filterRef.current = input;

							return true;
						});
					}}
					onTagsAppend={(tags: string[]) => {
						if (isTagsLoading) {
							return false;
						}

						// Filtering in lower case, because tags are not case sensitive.
						const currentTags = new Set([
							...newTags.map(t => t.toLocaleLowerCase()),
							...availableTags.map(t => t.tag.toLocaleLowerCase())
						]);
						const uniqueTags = tags.filter(tag => !currentTags.has(tag.toLocaleLowerCase()));
						if (uniqueTags.length > 0) {
							setNewTags([...newTags, ...uniqueTags]);
							return true;
						}

						return false;
					}}
					fontSize="0.875rem"
					ref={inputRef}
				/>
				<div className={classes.inputHelperText}>
					{localize('Make tags available to this library by entering them in the box above.')}
				</div>
			</div>
			{availableTagsElement}
		</>
	);

	return (
		<ModalWindow
			title={localize('Manage tags')}
			width="37.5rem"
			content={content}
			actions={actions}
			closeHandler={cancel}
		></ModalWindow>
	);
};

export default inject((stores: ILibraryMainStore) => ({
	libraryId: stores.viewModel.libraryId,
	tagsStore: stores.tagsStore,
	lookupApiService: stores.api.lookupApiService,
	userApiService: stores.api.userApiService,
	sortOrder: stores.userOptionsStore.manageTagsDialogSort,
	setSortOrder: stores.userOptionsStore.setManageTagsDialogSort,
	safeExecuteAsync: stores.safeExecuteAsync
}))(observer(ManageTagsDialog));
